import React, { useEffect, useRef, useState } from 'react';
import './Process.css';

const Process: React.FC = () => {
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Header observer
    const headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsHeaderVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px'
      }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }

    // Steps observers
    const observers = stepRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSteps(prev => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            } else {
              setVisibleSteps(prev => prev.filter(i => i !== index));
            }
          });
        },
        {
          threshold: 0.3,
          rootMargin: '0px'
        }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      if (headerRef.current) {
        headerObserver.unobserve(headerRef.current);
      }
      observers.forEach((observer, index) => {
        if (observer && stepRefs.current[index]) {
          observer.unobserve(stepRefs.current[index]!);
        }
      });
    };
  }, []);

  const steps = [
    {
      number: '1',
      title: 'Visit',
      description: 'We visit your space and understand your requirements to build a custom solution for you.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      number: '2',
      title: 'Propose',
      description: 'We provide a detailed proposal and implementation plan, ensuring transparency.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      number: '3',
      title: 'Implement',
      description: 'After you approve our quotation, we begin work immediately to convert your space into a smart one.',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <section className="process" id="process">
      <div className="process-container">
        <div className={`process-header ${isHeaderVisible ? 'header-animate-in' : 'header-animate-out'}`} ref={headerRef}>
          <span className="section-badge">OUR PROCESS</span>
          <h2 className="process-title">
            Simple, Easy and<br />
            Reliable!
          </h2>
        </div>

        <div className="process-steps">
          {steps.map((step, index) => (
            <div 
              key={index} 
              ref={el => stepRefs.current[index] = el}
              className={`process-step step-${index + 1} ${visibleSteps.includes(index) ? 'animate-in' : 'animate-out'}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="step-number-badge">
                <span className="step-number">{step.number}</span>
                <div className="number-circle"></div>
              </div>
              <div className="step-content">
                <div className="step-label">
                  <span className="step-tag">Step {step.number}</span>
                  <h3>{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
